import { Alignment, FollowParametricObject, mergeRecursive } from "@novorender/api";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadonlyVec3 } from "gl-matrix";

import { resetView, selectBookmark } from "features/render";
import { AsyncState, AsyncStatus } from "types/misc";

import { AlignmentId, AlignmentView, ColoringType, HorizontalDisplaySettings, TracerType } from "./types";

const initialState = {
    alignment: undefined as { name: string; id: number } | undefined,
    alignmentIds: { status: AsyncStatus.Initial } as AsyncState<AlignmentId[]>,
    alignments: { status: AsyncStatus.Initial } as AsyncState<{ [key: string]: Alignment }>,
    showTracer: "off" as TracerType,
    verticalClipping: false,
    autoRecenter: false,
    step: 1 as number,
    hasTracer2dDrawProducts: false,
    view: AlignmentView.Pinhole,
    showGrid: true,
    selectedStation: undefined as
        | {
              station: number;
              point: ReadonlyVec3;
              direction: ReadonlyVec3;
          }
        | undefined,
    horizontalDisplaySettings: {
        stations: {
            show: true,
            interval: 100,
            slope: false,
            elevation: false,
        },
        verticalSegment: {
            show: false,
            curvature: true,
            elevation: false,
            length: false,
            slope: false,
        },
        horizontalSegment: {
            show: true,
            curvature: true,
            elevation: false,
            length: false,
        },
        showPointsOfCurvature: true,
        showCurvatures: true,
        showAlignmentNames: false,
        coloringType: ColoringType.Curvature,
    } as HorizontalDisplaySettings,
    followObject: undefined as undefined | FollowParametricObject,
    // Communicates some legacy state from the bookmark and
    // and is resolved in useLoadAlignmentFromLegacyBookmark
    fromBookmark: undefined as
        | undefined
        | {
              // Coming from follow path widget
              profileNumber: number;
              landXmlPathId?: number;
          }
        | {
              // alignment.name was switched at some point to include project name
              alignment: { name: string; id: number };
          },
    showFollowPathRedirectionSnackbar: false,
};

type State = typeof initialState;

export const alignmentSlice = createSlice({
    name: "alignment",
    initialState: initialState,
    reducers: {
        setAlignment: (state, action: PayloadAction<State["alignment"]>) => {
            state.alignment = action.payload;
        },
        setAlignmentIds: (state, action: PayloadAction<State["alignmentIds"]>) => {
            state.alignmentIds = action.payload;
        },
        setAlignments: (state, action: PayloadAction<State["alignments"]>) => {
            state.alignments = action.payload;
        },
        setSelectedStation: (state, action: PayloadAction<State["selectedStation"]>) => {
            state.selectedStation = action.payload;
        },
        updateDisplaySettings: (state, action: PayloadAction<Partial<HorizontalDisplaySettings>>) => {
            state.horizontalDisplaySettings = mergeRecursive(state.horizontalDisplaySettings, action.payload);
        },
        setShowTracer: (state, action: PayloadAction<State["showTracer"]>) => {
            state.showTracer = action.payload;
        },
        setVerticalClipping: (state, action: PayloadAction<State["verticalClipping"]>) => {
            state.verticalClipping = action.payload;
        },
        setAutoRecenter: (state, action: PayloadAction<State["autoRecenter"]>) => {
            state.autoRecenter = action.payload;
        },
        setFollowObject: (state, action: PayloadAction<State["followObject"]>) => {
            state.followObject = action.payload;
        },
        setStep: (state, action: PayloadAction<State["step"]>) => {
            state.step = action.payload;
        },
        setHasTracer2dDrawProducts: (state, action: PayloadAction<State["hasTracer2dDrawProducts"]>) => {
            state.hasTracer2dDrawProducts = action.payload;
        },
        setAlignmentView: (state, action: PayloadAction<State["view"]>) => {
            state.view = action.payload;
        },
        setShowGrid: (state, action: PayloadAction<State["showGrid"]>) => {
            state.showGrid = action.payload;
        },
        setFromBookmark: (state, action: PayloadAction<State["fromBookmark"]>) => {
            state.fromBookmark = action.payload;
        },
        setShowFollowPathRedirectionSnackbar: (
            state,
            action: PayloadAction<State["showFollowPathRedirectionSnackbar"]>,
        ) => {
            state.showFollowPathRedirectionSnackbar = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(selectBookmark, (state, action) => {
            const { followPath: fp, alignment } = action.payload;

            if (alignment) {
                // Don't set alignment straight away in case alignment.name has old format
                state.fromBookmark = {
                    alignment: alignment.alignment,
                };
                state.selectedStation = alignment.selectedStation;
                state.verticalClipping = alignment.verticalClipping ?? false;
                state.followObject = alignment.followObject;
            } else if (fp) {
                state.fromBookmark = {
                    profileNumber: fp.profileNumber,
                    landXmlPathId: fp.selected.landXmlPathId,
                };
                state.selectedStation = undefined;
                state.verticalClipping = fp.verticalClipping ?? false;
                state.followObject = fp.followObject;
            } else {
                resetAlignmentState(state);
            }
        });
        builder.addCase(resetView, (state) => {
            resetAlignmentState(state);
        });
    },
});

function resetAlignmentState(state: State) {
    state.alignment = undefined;
    state.fromBookmark = undefined;
    state.selectedStation = undefined;
    state.verticalClipping = false;
    state.followObject = undefined;
}

const { actions, reducer } = alignmentSlice;
export { actions as alignmentActions, reducer as alignmentReducer };
